import { makeStyles } from 'tss-react/mui';
import { TimesheetDay, TimesheetDayInterval } from 'api/report/types';
import Typography from '@mui/material/Typography';
import TextField, { Label } from 'components/interface/textField/TextField';
import { red } from '@mui/material/colors';
import { lighten } from '@mui/material';
import NumberField from 'components/interface/textField/formats/NumberField';
import Symbol from 'components/Symbol';

const useStyles = makeStyles()(theme => ({
    base: {
        width: '100%',
    },
    row: {
        'width': '100%',
        '& + &': {
            marginTop: theme.spacing(2),
        },
    },
    sickHoursLabel: {
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(1),
    },
    isSickHours: {
        background: lighten(red[50], 0.4),
    },
    sickHoursIcon: {
        color: red[900],
    },
}));

interface Props {
    day: TimesheetDay | undefined;
    dayIndex: number;
    totalHours: string;
    canSubmit: boolean;
    disabled: boolean;
    loading: boolean;
    enableSickHours: boolean;
    onUpdateTimesheetDay: (
        updateFunc: (day: TimesheetDay) => TimesheetDay,
        index: number
    ) => void;
}

export default function StartStopTimesheet({
    day,
    dayIndex,
    totalHours,
    canSubmit,
    disabled,
    enableSickHours,
    onUpdateTimesheetDay,
}: Props): JSX.Element {
    const { classes } = useStyles();

    if (!day) {
        return <></>;
    }

    const handleUpdateTimesheetDay = (
        value: Partial<TimesheetDayInterval>,
        columnIndex: number,
        isSickHours: boolean
    ) => {
        onUpdateTimesheetDay(prev => {
            const d = { ...prev };
            d.intervals = [...d.intervals];
            d.intervals[columnIndex] = {
                ...d.intervals[columnIndex],
                ...value,
                isSickHours,
            };

            return d;
        }, dayIndex);
    };

    const renderInterval = (
        value: TimesheetDayInterval,
        intervalIndex: number
    ) => (
        <>
            {value.isSickHours ? (
                <div className={classes.sickHoursLabel}>
                    <Typography variant='body1' color='textPrimary'>
                        <b>Sjuktimmar</b>
                    </Typography>
                </div>
            ) : null}
            <div className={classes.row}>
                <TextField
                    label='Start'
                    value={value.start || ''}
                    onChange={v =>
                        handleUpdateTimesheetDay(
                            { start: v },
                            intervalIndex,
                            value.isSickHours
                        )
                    }
                    type='time'
                    error={!value.start && !!value.stop}
                    disabled={!canSubmit || disabled}
                    fullWidth
                    variantSx={{
                        backgroundColor: value.isSickHours
                            ? lighten(red[50], 0.4)
                            : undefined,
                    }}
                    startAdornment={
                        value.isSickHours ? (
                            <Symbol
                                symbol='sick'
                                color='red-dark'
                                size='lg'
                                tooltip='Sjuktimmar'
                                sx={{
                                    paddingRight: 2,
                                }}
                            />
                        ) : null
                    }
                />
            </div>
            <div className={classes.row}>
                <TextField
                    label='Stop'
                    value={value.stop || ''}
                    onChange={v =>
                        handleUpdateTimesheetDay(
                            { stop: v },
                            intervalIndex,
                            value.isSickHours
                        )
                    }
                    type='time'
                    error={!value.stop && !!value.start}
                    disabled={!canSubmit || disabled}
                    fullWidth
                    variantSx={{
                        backgroundColor: value.isSickHours
                            ? lighten(red[50], 0.4)
                            : undefined,
                    }}
                    startAdornment={
                        value.isSickHours ? (
                            <Symbol
                                symbol='sick'
                                color='red-dark'
                                size='lg'
                                tooltip='Sjuktimmar'
                                sx={{
                                    paddingRight: 2,
                                }}
                            />
                        ) : null
                    }
                />
            </div>
            {value.isSickHours ? null : (
                <div className={classes.row}>
                    <NumberField
                        label='Rast'
                        value={value.breakInMinutes || 0}
                        onChange={v =>
                            handleUpdateTimesheetDay(
                                { breakInMinutes: v },
                                intervalIndex,
                                value.isSickHours
                            )
                        }
                        endAdornment={
                            <Typography variant='body2' color='inherit'>
                                minuter
                            </Typography>
                        }
                        disabled={!canSubmit || disabled}
                        allowZero
                        maxValue={300}
                        fullWidth
                    />
                </div>
            )}
        </>
    );

    return (
        <div className={classes.base}>
            {renderInterval(
                day.intervals && day.intervals[0]
                    ? day.intervals[0]
                    : {
                          start: '',
                          stop: '',
                          breakInMinutes: 0,
                          isSickHours: false,
                      },
                0
            )}
            {enableSickHours
                ? renderInterval(
                      day.intervals && day.intervals[1]
                          ? day.intervals[1]
                          : {
                                start: '',
                                stop: '',
                                breakInMinutes: 0,
                                isSickHours: true,
                            },
                      1
                  )
                : null}
            <div className={classes.row}>
                <Label text='Totalt antal timmar' />
                <Typography variant='body1' color='textPrimary'>
                    <b>{totalHours || '-'}</b>
                </Typography>
            </div>
        </div>
    );
}
